import { z } from "zod";

/* ===========================================================================
 * GET /v1/funds/:id — Navigate Fund from Group Profile (US-E4.F3-0021 / @UC0055).
 *
 * Cross-check API_BUILD_PLAN Phase 4 (Funds): plan liệt kê `POST|GET /v1/groups/:id/funds`
 * + `PATCH /v1/funds/:id`. Slice 0021 chỉ cần READ 1 fund (`GET /v1/funds/:id`) — member-of-group
 * guard, để mobile quyết điều hướng Fund Dashboard (đã config) vs Fund Configuration (chưa config).
 * KHÔNG build POST/PATCH funds/settings ở đây (Phase 4/5 full deferred — SD-2 slice tối thiểu).
 *
 * Một nguồn sự thật cho api (shape response + validate biên) và mobile (gọi + map) — DRY.
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - PII / tenant isolation (C2/C4): chỉ member ACTIVE của group chứa fund xem được (server enforce
 *    qua FundAccessGuard — resolve fund→group→member). Non-member/cross-tenant → 404 đồng nhất.
 *  - ⚠️ KHÔNG có field SỐ DƯ/tiền: số dư phụ thuộc Ledger Phase 6 (chưa build) — CẮT phạm vi,
 *    KHÔNG bịa giá trị tài chính (guardrail A4/B1).
 *  - needsConfig: cờ "chưa cấu hình tài khoản ngân hàng" (Fund.bankAccount null/rỗng) — client
 *    quyết Dashboard (false) vs Configuration (true). KHÔNG lộ nội dung bankAccount (chỉ cờ boolean).
 *  - `.strict()` chặn rò field thừa (balance/bankAccount/settings/secret) ra response.
 * ========================================================================= */

/* ---------------------------------------------------------------------------
 * FundType + per-type sub-config (US-E13.F2-0105-S09 / @UC0031 — @gate MONEY/SCHEMA).
 *
 * Vấn đề (FINDINGS F4): create-fund CHỈ nhận {name}; UI dựng đủ 3 loại + field nhưng bị DROP
 * → mọi quỹ giống hệt. Bổ sung `type` (recurring|goal|oneoff) + sub-config theo loại. Sub-config
 * lưu vào Fund.settings jsonb dưới key `fund_type_config` (KHÔNG thêm nhiều cột — additive tối thiểu).
 *
 * B1 tiền: feeMinor/targetMinor/totalMinor là STRING số nguyên dương (BigInt-safe, server parse) —
 * KHÔNG float ở bất kỳ tầng nào (mirror amountMinor collections/expenses). Trần PG signed BIGINT.
 * Wire value enum cycle/split KHỚP mobile (create_fund_page FundPeriod/FundSplitMode.wireValue).
 * ------------------------------------------------------------------------- */

/** Loại quỹ (một nguồn sự thật api↔mobile — khớp create_fund_page FundTypeTone). */
export const FUND_TYPE = {
  RECURRING: "recurring",
  GOAL: "goal",
  ONEOFF: "oneoff",
} as const;

/** Enum FundType (biên request). Giá trị lạ → reject. */
export const fundTypeSchema = z.enum([
  FUND_TYPE.RECURRING,
  FUND_TYPE.GOAL,
  FUND_TYPE.ONEOFF,
]);

export type FundType = z.infer<typeof fundTypeSchema>;

/** Chu kỳ recurring (khớp mobile FundPeriod.wireValue: monthly|quarterly). */
export const FUND_CYCLE = {
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
} as const;

export const fundCycleSchema = z.enum([FUND_CYCLE.MONTHLY, FUND_CYCLE.QUARTERLY]);

export type FundCycle = z.infer<typeof fundCycleSchema>;

/** Cách chia oneoff (khớp mobile FundSplitMode.wireValue: equal|per_head|custom). */
export const FUND_SPLIT_MODE = {
  EQUAL: "equal",
  PER_HEAD: "per_head",
  CUSTOM: "custom",
} as const;

export const fundSplitModeSchema = z.enum([
  FUND_SPLIT_MODE.EQUAL,
  FUND_SPLIT_MODE.PER_HEAD,
  FUND_SPLIT_MODE.CUSTOM,
]);

export type FundSplitMode = z.infer<typeof fundSplitModeSchema>;

/** Ngày chốt recurring trong tháng (1..28 — cận trên 28 để tháng 2 luôn hợp lệ, tránh 29/30/31). */
export const FUND_CLOSE_DAY_MIN = 1;
export const FUND_CLOSE_DAY_MAX = 28;

/** Key jsonb lưu sub-config theo loại trong Fund.settings (KHÔNG thêm cột — additive tối thiểu). */
export const FUND_TYPE_CONFIG_KEY = "fund_type_config";

/** Số chữ số tối đa amount (PG BIGINT max 19 chữ số). Chuỗi dài hơn ghi DB P2020 → 500. */
const FUND_AMOUNT_MAX_DIGITS = 19;
/** Trần PG signed BIGINT (9_223_372_036_854_775_807) — amount vượt → reject (chống overflow). */
const FUND_PG_BIGINT_MAX = 9223372036854775807n;
/** Số nguyên dạng string (amount BigInt) — không dấu chấm, không số mũ, không dấu âm. */
const FUND_INTEGER_STRING_PATTERN = /^\d+$/;

/** Thông điệp lỗi tiền (một nguồn sự thật api↔mobile). */
export const FUND_AMOUNT_REQUIRED_MESSAGE = "Vui lòng nhập số tiền";
export const FUND_AMOUNT_INVALID_MESSAGE = "Số tiền phải là số nguyên dương (đơn vị nhỏ nhất)";
export const FUND_DEADLINE_INVALID_MESSAGE = "Hạn chót không hợp lệ (định dạng YYYY-MM-DD)";

/** Regex 'YYYY-MM-DD' (date-only) cho deadline goal. */
const FUND_ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** True nếu 'YYYY-MM-DD' là ngày dương lịch THẬT (chặn '2026-02-30'). */
function isRealCalendarDate(value: string): boolean {
  const [y, m, d] = value.split("-").map((p) => Number.parseInt(p, 10));
  if (y === undefined || m === undefined || d === undefined) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (
    dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
  );
}

/**
 * Schema tiền minor STRING BigInt-safe > 0 (B1 — no float). Rỗng/âm/float/số mũ → reject; giá trị
 * THẬT vượt trần PG BIGINT → reject (chống ghi DB P2020→500). Dùng cho fee/target/total. DRY 3 loại.
 */
export const fundMoneyMinorSchema = z
  .string()
  .trim()
  .min(1, FUND_AMOUNT_REQUIRED_MESSAGE)
  .max(FUND_AMOUNT_MAX_DIGITS, FUND_AMOUNT_INVALID_MESSAGE)
  .regex(FUND_INTEGER_STRING_PATTERN, FUND_AMOUNT_INVALID_MESSAGE)
  .refine((v) => {
    // BigInt() ném với chuỗi không-số ⇒ trả false (invalid), KHÔNG throw.
    try {
      return BigInt(v) > 0n && BigInt(v) <= FUND_PG_BIGINT_MAX;
    } catch {
      return false;
    }
  }, FUND_AMOUNT_INVALID_MESSAGE);

/**
 * Shape response cho GET /v1/funds/:id (chi tiết 1 quỹ — KHÔNG số dư):
 *  - id/name: định danh + tên quỹ (hiển thị ở Fund Dashboard/Configuration shell).
 *  - currency: mã tiền tệ ISO 4217 của quỹ (FIELD HIỂN THỊ, KHÔNG phải số tiền/minor-unit → không B1).
 *  - groupId: nhóm chứa quỹ (để client biết ngữ cảnh nhóm).
 *  - needsConfig: chưa cấu hình bankAccount → client điều hướng Fund Configuration (AC2); ngược lại
 *    Fund Dashboard (AC1). Server là single source of truth — client KHÔNG tự đoán.
 *  - isArchived: quỹ đã lưu trữ (vẫn xem được; client có thể hiển thị trạng thái lưu trữ).
 *  - type: FundType đã lưu (recurring|goal|oneoff) — null khi quỹ tạo trước S09 (back-compat).
 *  - typeConfig: sub-config theo loại (fee/cycle/closeDay · target/deadline · total/split). Money
 *    trả dạng STRING minor (B1 — KHÔNG float). null khi vắng. `.passthrough()` KHÔNG dùng — shape cố
 *    định per-type nhưng để mềm cho back-compat, dùng object optional field.
 * `.strict()` chặn rò field tài chính/secret (balance/bankAccount) ra response (C2).
 */
export const fundTypeConfigResponseSchema = z
  .object({
    // recurring
    feeMinor: z.string().regex(FUND_INTEGER_STRING_PATTERN).optional(),
    cycle: fundCycleSchema.optional(),
    closeDay: z.number().int().min(FUND_CLOSE_DAY_MIN).max(FUND_CLOSE_DAY_MAX).optional(),
    // goal
    targetMinor: z.string().regex(FUND_INTEGER_STRING_PATTERN).optional(),
    deadline: z.string().regex(FUND_ISO_DATE_PATTERN).nullable().optional(),
    // oneoff
    totalMinor: z.string().regex(FUND_INTEGER_STRING_PATTERN).optional(),
    splitMode: fundSplitModeSchema.optional(),
  })
  .strict();

export type FundTypeConfigResponse = z.infer<typeof fundTypeConfigResponseSchema>;

export const fundDetailResponseSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    currency: z.string().length(3),
    groupId: z.string().uuid(),
    needsConfig: z.boolean(),
    isArchived: z.boolean(),
    // S09: type + sub-config (null khi quỹ cũ tạo trước migration — back-compat, mobile đọc an toàn).
    type: fundTypeSchema.nullable().optional(),
    typeConfig: fundTypeConfigResponseSchema.nullable().optional(),
  })
  .strict();

export type FundDetailResponse = z.infer<typeof fundDetailResponseSchema>;

/* ===========================================================================
 * GET /v1/funds — List caller's funds (US-E13.F0-0082-be-wiring / @UC0015 · @UC0055).
 *
 * Gap E2E: mobile Funds tab gọi `GET /v1/funds` (listFunds → { data: [Fund...] }) nhưng BE chỉ có
 * `GET /v1/funds/:id`. Bổ sung list endpoint membership-scoped (IDOR-safe): trả MỌI fund thuộc group
 * mà caller là member ACTIVE. userId LẤY TỪ token (UserAuthGuard) — KHÔNG nhận từ query.
 *
 * SHAPE:
 *  - id/name/currency: định danh + hiển thị (currency ISO 4217, FIELD hiển thị — không B1).
 *  - balanceMinor: SỐ DƯ CASH của quỹ (O(1) từ account_balances, KHÔNG SUM hot-path).
 *
 * 🔴 US-E24.F28-0383 (@UC9628) — GỠ HAI SAI LỆCH cùng lúc (audit 11/08/2026):
 *
 *  (a) `z.number().int()` → **chuỗi minor units**. DEVIATION cũ được biện minh bằng *"model
 *      `Fund.fromJson` của apps/mobile cố định đọc `as int` — KHÔNG đổi mobile"*. Lý do đó **đã hết
 *      hiệu lực**: `apps/mobile` bị thay nguyên khối (PR #324) và bản hiện tại **không có
 *      `fund.dart`, cũng không gọi `GET /v1/funds`** — consumer DUY NHẤT của schema này là int-spec
 *      của chính API. Giữ number chỉ còn là một ngoại lệ B1 không ai cần, và mọi tài liệu client
 *      phải mang thêm một dòng cảnh báo vĩnh viễn.
 *
 *  (b) Redaction `0` → **discriminated union theo `redacted`** (mirror `myGroupListItemSchema`).
 *      Bản cũ trả `balanceMinor: 0` và item **không có cờ nào** ⇒ trên wire *"quỹ rỗng"* và *"bạn
 *      không được xem số dư"* **giống hệt nhau**; member thường ở quỹ private thấy nhóm mình trắng
 *      tay. §3.8: `0` là NÓI DỐI về tiền. Nhánh ẩn mang literal `'•••'` ⇒ trạng thái sai
 *      **không biểu diễn được** (không thể vừa `redacted:true` vừa mang số thật).
 *  - groupId: nhóm chứa quỹ (mobile gộp theo nhóm; null→"Quỹ của tôi").
 *  - role: vai trò caller trong quỹ = GroupMember.role của group chứa fund (owner|admin|treasurer|member).
 *  - type/goalTargetMinor: CỐ Ý VẮNG — schema Fund KHÔNG có cột type/goal (KHÔNG bịa field; mobile đọc
 *    an toàn vắng ⇒ null → UI fallback recurring, không vẽ progress).
 * `.strict()` mỗi item chặn rò field thừa (bankAccount/settings/secret).
 * ========================================================================= */

/** Vai trò membership hợp lệ (khớp GroupMember.role: owner|admin|treasurer|member). */
export const FUND_LIST_ROLES = ["owner", "admin", "treasurer", "member"] as const;

/**
 * Placeholder cho số dư bị che. Mirror `PUBLIC_STATEMENT_REDACTED` (compliance.ts) và
 * `GROUP_MONEY_REDACTED` (groups.ts) — **CÙNG một placeholder trên toàn API**, client chỉ phải
 * học một quy ước.
 */
export const FUND_MONEY_REDACTED = "•••" as const;

/** Tiền dạng chuỗi minor units (B1). Số dư CASH có thể âm ⇒ cho phép dấu `-`. */
const FUND_MONEY_INTEGER_STRING = z.string().regex(/^-?(0|[1-9][0-9]*)$/);

/**
 * Phần KHÔNG phụ thuộc redaction của 1 item danh sách quỹ.
 *
 * needsConfig: cờ boolean per-fund (bankAccount rỗng ⇒ true) — CÙNG nguồn với GET /v1/funds/:id
 * (isFundUnconfigured). Cho phép list hiển thị badge "Chưa cấu hình" TRƯỚC khi tap (parity với
 * Group Profile) → user biết trước quỹ nào rơi vào màn Configuration thay vì Dashboard. KHÔNG lộ
 * bankAccount thô (chỉ cờ boolean — C2).
 */
const fundListItemBase = {
  id: z.string().uuid(),
  name: z.string(),
  currency: z.string().length(3),
  groupId: z.string().uuid(),
  role: z.enum(FUND_LIST_ROLES),
  needsConfig: z.boolean(),
};

/**
 * 1 item trong danh sách quỹ của caller — DISCRIMINATED UNION theo `redacted` (xem khối doc ở trên).
 * `.strict()` mỗi nhánh chặn rò field thừa (bankAccount/settings/secret).
 */
export const fundListItemSchema = z.discriminatedUnion("redacted", [
  z
    .object({
      ...fundListItemBase,
      redacted: z.literal(false),
      balanceMinor: FUND_MONEY_INTEGER_STRING,
    })
    .strict(),
  z
    .object({
      ...fundListItemBase,
      redacted: z.literal(true),
      balanceMinor: z.literal(FUND_MONEY_REDACTED),
    })
    .strict(),
]);

export type FundListItem = z.infer<typeof fundListItemSchema>;

/** Envelope { data: [...] } cho GET /v1/funds (mobile listFunds đọc `json['data']`). `.strict()`. */
export const fundsListResponseSchema = z
  .object({
    data: z.array(fundListItemSchema),
  })
  .strict();

export type FundsListResponse = z.infer<typeof fundsListResponseSchema>;

/* ===========================================================================
 * POST /v1/groups/:id/funds — Create New Fund (US-E5.F1-0031 / @UC0031, Phase 4).
 *
 * Cross-check API_BUILD_PLAN Phase 4 (Funds): `POST|GET /v1/groups/:id/funds`. Slice 0031 = TỐI
 * THIỂU: chỉ TẠO 1 fund shell trong group (RBAC OWNER/ADMIN). GET list / PATCH / archive → ngoài
 * phạm vi (story Funds khác / 0032 PATCH bank).
 *
 * Một nguồn sự thật cho api (validate biên) và mobile (gọi + counter) — DRY.
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - `.strict()` chặn over-post: client KHÔNG truyền currency/groupId/role/id từ body. currency
 *    KẾ THỪA `group.defaultCurrency` (server đọc từ group row); groupId từ `:id` param; creator
 *    từ token. KHÔNG nhận từ body (chống IDOR/over-post — C5).
 *  - ⚠️ KHÔNG ghi ledger / KHÔNG số tiền (chỉ tạo Fund shell, bankAccount null → needsConfig=true).
 *    currency là FIELD HIỂN THỊ (mã ISO 4217), KHÔNG phải số tiền/minor-unit → không B1.
 *  - Response shape = fundDetailResponseSchema (id/name/currency/groupId/needsConfig/isArchived).
 * ========================================================================= */

/** Giới hạn độ dài tên quỹ (US-E5.F1-0031 / @UC0031 — invariant story ≤50 ký tự, AC3). */
export const FUND_NAME_MAX_LENGTH = 50;

/** Thông điệp lỗi khi tên quỹ trống (AC2) — một nguồn sự thật api ↔ mobile. */
export const FUND_NAME_REQUIRED_MESSAGE = "Vui lòng nhập tên quỹ";

/** Thông điệp lỗi khi tên quỹ vượt 50 ký tự (AC3). */
export const FUND_NAME_TOO_LONG_MESSAGE = "Tên quỹ tối đa 50 ký tự";

/**
 * Body cho POST /v1/groups/:id/funds.
 * - name BẮT BUỘC: trim trước rồi validate 1..50 ký tự (đếm SAU trim).
 *   Rỗng/chỉ-khoảng-trắng → reject (AC2); >50 → reject (AC3).
 * - type OPTIONAL (S09 back-compat): vắng ⇒ hành vi cũ (chỉ name). Có ⇒ superRefine ép sub-config
 *   ĐÚNG theo loại (mirror fundConfigPatchSchema.superRefine):
 *     recurring ⇒ { feeMinor (>0 minor string), cycle (monthly|quarterly), closeDay (1..28) } BẮT BUỘC.
 *     goal      ⇒ { targetMinor (>0 minor string) } BẮT BUỘC; deadline (YYYY-MM-DD) OPTIONAL.
 *     oneoff    ⇒ { totalMinor (>0 minor string), splitMode (equal|per_head|custom) } BẮT BUỘC.
 *   Field sub-config của loại KHÁC (mồ côi) → reject (chống ghi rác chéo loại).
 * - B1 tiền: fee/target/total STRING minor BigInt-safe (fundMoneyMinorSchema) — server parse, no float.
 * - `.strict()` chặn field lạ tại biên (chống over-post/IDOR: KHÔNG nhận currency/groupId/role/id —
 *   currency kế thừa group, groupId từ param, role từ guard, id do server sinh).
 */
export const createFundRequestSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, FUND_NAME_REQUIRED_MESSAGE)
      .max(FUND_NAME_MAX_LENGTH, FUND_NAME_TOO_LONG_MESSAGE),
    type: fundTypeSchema.optional(),
    // recurring sub-config
    feeMinor: fundMoneyMinorSchema.optional(),
    cycle: fundCycleSchema.optional(),
    closeDay: z
      .number()
      .int()
      .min(FUND_CLOSE_DAY_MIN)
      .max(FUND_CLOSE_DAY_MAX)
      .optional(),
    // goal sub-config
    targetMinor: fundMoneyMinorSchema.optional(),
    deadline: z
      .string()
      .trim()
      .regex(FUND_ISO_DATE_PATTERN, FUND_DEADLINE_INVALID_MESSAGE)
      .refine(isRealCalendarDate, FUND_DEADLINE_INVALID_MESSAGE)
      .optional(),
    // oneoff sub-config
    totalMinor: fundMoneyMinorSchema.optional(),
    splitMode: fundSplitModeSchema.optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    // type vắng ⇒ back-compat (chỉ name). Sub-config mồ côi (không type) → reject.
    const hasAnySubConfig =
      body.feeMinor !== undefined ||
      body.cycle !== undefined ||
      body.closeDay !== undefined ||
      body.targetMinor !== undefined ||
      body.deadline !== undefined ||
      body.totalMinor !== undefined ||
      body.splitMode !== undefined;

    if (body.type === undefined) {
      if (hasAnySubConfig) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["type"],
          message: "Cần chọn loại quỹ khi có cấu hình theo loại",
        });
      }
      return;
    }

    // Helper: yêu cầu field có mặt.
    const require = (key: keyof typeof body, message: string): void => {
      if (body[key] === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key], message });
      }
    };
    // Helper: cấm field mồ côi (thuộc loại khác).
    const forbid = (key: keyof typeof body): void => {
      if (body[key] !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: "Cấu hình không thuộc loại quỹ đã chọn",
        });
      }
    };

    if (body.type === FUND_TYPE.RECURRING) {
      require("feeMinor", "Vui lòng nhập mức phí mỗi kỳ");
      require("cycle", "Vui lòng chọn chu kỳ");
      require("closeDay", "Vui lòng nhập ngày chốt (1–28)");
      forbid("targetMinor");
      forbid("deadline");
      forbid("totalMinor");
      forbid("splitMode");
      return;
    }
    if (body.type === FUND_TYPE.GOAL) {
      require("targetMinor", "Vui lòng nhập số tiền mục tiêu");
      // deadline OPTIONAL (goal có thể không đặt hạn).
      forbid("feeMinor");
      forbid("cycle");
      forbid("closeDay");
      forbid("totalMinor");
      forbid("splitMode");
      return;
    }
    // oneoff
    require("totalMinor", "Vui lòng nhập tổng chi phí");
    require("splitMode", "Vui lòng chọn cách chia");
    forbid("feeMinor");
    forbid("cycle");
    forbid("closeDay");
    forbid("targetMinor");
    forbid("deadline");
  });

export type CreateFundRequest = z.infer<typeof createFundRequestSchema>;

/* ===========================================================================
 * PATCH /v1/funds/:id/config — Configure Fund Confirmation Policy
 * (US-E5.F2-0033 / @UC0044, Phase 5 — Config resolver E13, CỬA NGÕ TIỀN).
 *
 * Cross-check API_BUILD_PLAN Phase 5 (@gate CONFIG): ghi override config mức quỹ vào
 * `Fund.settings` (jsonb) qua đường validate `allowed` (SystemSetting). Slice 0033 = 1 key
 * `payment_confirmation_mode`; 0034 BỒI ĐẮP thêm `expense_decision_mode` vào CÙNG schema này
 * (thiết kế extensible: body partial, mỗi key optional → thêm key mới không vỡ client cũ).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - CỬA NGÕ TIỀN: value phải ∈ `allowed` của SystemSetting (FAIL-CLOSED). Schema biên chỉ nhận
 *    subset UI-allowed (`two_party | admin_one_party`); `gateway_auto` (allowed thứ 3 trong system)
 *    NGOÀI scope UI story này → KHÔNG hiện ở body schema. Server VẪN validate lại theo SystemSetting.
 *  - `.strict()` chặn over-post (chống ghi rác key lạ vào Fund.settings jsonb).
 *  - body partial: ít nhất 1 key (refine) để PATCH rỗng → 400 (không cho no-op silent).
 *  - KHÔNG ghi ledger / KHÔNG số tiền — chỉ ghi config (đổi mode CHỈ áp đợt thu MỚI; enforce ⇐
 *    Collections Phase 7). currency/money KHÔNG liên quan → không B1.
 * ========================================================================= */

/**
 * Hằng mode xác nhận tiền vào (một nguồn sự thật api↔mobile — tránh magic-string).
 * `GATEWAY_AUTO` (US-E10.F2-0076 · @UC0057): mode thứ 3 — cổng thanh toán tự động confirm qua webhook.
 * KHÔNG nằm trong UI-subset chọn được ở app (`paymentConfirmationModeSchema`) — SUPER_ADMIN bật ở
 * system-settings; hằng này dùng cho luồng webhook backend so khớp mode hiệu lực.
 */
export const PAYMENT_CONFIRMATION_MODE = {
  TWO_PARTY: "two_party",
  ADMIN_ONE_PARTY: "admin_one_party",
  GATEWAY_AUTO: "gateway_auto",
} as const;

/** Key SystemSetting cho confirmation policy (validate `allowed` ở server). */
export const PAYMENT_CONFIRMATION_MODE_KEY = "payment_confirmation_mode";

/**
 * Subset UI-allowed cho `payment_confirmation_mode` (story chỉ 2-đầu / 1-đầu). `gateway_auto`
 * ngoài scope UI (gateway = E10 webhook) → KHÔNG nhận từ body, nhưng SystemSetting vẫn allow.
 */
export const paymentConfirmationModeSchema = z.enum([
  PAYMENT_CONFIRMATION_MODE.TWO_PARTY,
  PAYMENT_CONFIRMATION_MODE.ADMIN_ONE_PARTY,
]);

export type PaymentConfirmationMode = z.infer<typeof paymentConfirmationModeSchema>;

/* ---------------------------------------------------------------------------
 * Expense Decision Policy (US-E5.F2-0034 / @UC0045, Phase 5 Config — bồi đắp 0033).
 *
 * Story dùng nhãn user-facing "admin-only / vote". Hệ thống (SystemSetting `expense_decision_mode`,
 * TDD §6.4b) allowed = {majority, ratio, admin_only}. MAP:
 *  - UI "admin-only" → `admin_only`  (Admin/Treasurer tự quyết — DEFAULT, AC1/AC4).
 *  - UI "vote" (kèm tỉ lệ%) → `ratio` (threshold = tỉ lệ thông qua, deadline = thời hạn — AC2/AC3).
 *  - `majority` cũng được expose (∈ system allowed) — vote đa số đơn giản, KHÔNG cần threshold/deadline.
 * Cả 3 mode đều ∈ system allowed → expose ở contract enum. CHỈ `ratio` BẮT BUỘC threshold + deadline.
 *
 * RANH GIỚI (0034 CHỈ STORE config): KHÔNG mở phiên vote / KHÔNG tính consensus PASS-FAIL (E7).
 * --------------------------------------------------------------------------- */

/** Hằng mode quyết định khoản chi (một nguồn sự thật api↔mobile). */
export const EXPENSE_DECISION_MODE = {
  ADMIN_ONLY: "admin_only",
  MAJORITY: "majority",
  RATIO: "ratio",
} as const;

/** Key SystemSetting cho expense-decision (validate `allowed` ở server). */
export const EXPENSE_DECISION_MODE_KEY = "expense_decision_mode";
export const EXPENSE_DECISION_THRESHOLD_KEY = "expense_decision_threshold";
export const VOTE_MIN_DEADLINE_HOURS_KEY = "vote_min_deadline_hours";

/** Biên ngưỡng tỉ lệ thông qua (chỉ khi ratio) — khớp SystemSetting.allowed {min:0.5,max:1.0}. */
export const EXPENSE_DECISION_THRESHOLD_MIN = 0.5;
export const EXPENSE_DECISION_THRESHOLD_MAX = 1.0;
/** Biên thời hạn vote (giờ) — khớp SystemSetting.allowed {min:1,max:720}. */
export const VOTE_MIN_DEADLINE_HOURS_MIN = 1;
export const VOTE_MIN_DEADLINE_HOURS_MAX = 720;

/** Enum mode quyết chi — cả 3 ∈ SystemSetting.allowed (UI-subset = system allowed ở đây). */
export const expenseDecisionModeSchema = z.enum([
  EXPENSE_DECISION_MODE.ADMIN_ONLY,
  EXPENSE_DECISION_MODE.MAJORITY,
  EXPENSE_DECISION_MODE.RATIO,
]);

export type ExpenseDecisionMode = z.infer<typeof expenseDecisionModeSchema>;

/** Ngưỡng tỉ lệ (0.5–1.0) — biên khớp SystemSetting.allowed; service validate LẠI fail-closed. */
export const expenseDecisionThresholdSchema = z
  .number()
  .min(EXPENSE_DECISION_THRESHOLD_MIN)
  .max(EXPENSE_DECISION_THRESHOLD_MAX);

/** Thời hạn vote (giờ, số nguyên 1–720). */
export const voteMinDeadlineHoursSchema = z
  .number()
  .int()
  .min(VOTE_MIN_DEADLINE_HOURS_MIN)
  .max(VOTE_MIN_DEADLINE_HOURS_MAX);

/* ---------------------------------------------------------------------------
 * US-E5.F2-0084 — 5 key group-overridable còn thiếu đường ghi vào PATCH config.
 * Bổ sung "Cài đặt nhóm" đầy đủ (yêu cầu user 06/07). Wire-key KHỚP SystemSetting seed
 * (packages/db/prisma/seed.ts). Mỗi schema là biên client; service validate LẠI value ∈
 * SystemSetting.allowed (fail-closed — cửa ngõ tiền). Schema `transparency_mode` /
 * `reminder_days_before` / `overdue_daily_reminder` / `ai_token_budget_monthly` đã có nguồn
 * canonical ở contract khác — ở đây CO-LOCATE biên cho PATCH config (giữ funds.ts tự chứa,
 * tránh import vòng trong package). `pay_on_behalf_creates_debt` là key MỚI (chưa từng export).
 * --------------------------------------------------------------------------- */

/** transparency_mode: minh bạch tài chính cho member thường (@UC0025). Default private (fail-closed). */
export const TRANSPARENCY_MODE_KEY = "transparency_mode";
export const transparencyModePatchSchema = z.enum(["public", "private"]);

/** reminder_days_before: số ngày trước hạn để nhắc (int [0,30]). */
export const REMINDER_DAYS_BEFORE_KEY = "reminder_days_before";
export const REMINDER_DAYS_BEFORE_MIN = 0;
export const REMINDER_DAYS_BEFORE_MAX = 30;
export const reminderDaysBeforePatchSchema = z
  .number()
  .int()
  .min(REMINDER_DAYS_BEFORE_MIN)
  .max(REMINDER_DAYS_BEFORE_MAX);

/** overdue_daily_reminder: nhắc quá hạn hằng ngày (true) hay 1 lần (false). */
export const OVERDUE_DAILY_REMINDER_KEY = "overdue_daily_reminder";
export const overdueDailyReminderPatchSchema = z.boolean();

/** pay_on_behalf_creates_debt: đóng hộ có sinh nợ A↔B (true) hay coi như tặng (false). MONEY. */
export const PAY_ON_BEHALF_CREATES_DEBT_KEY = "pay_on_behalf_creates_debt";
export const payOnBehalfCreatesDebtPatchSchema = z.boolean();

/* ---------------------------------------------------------------------------
 * US-E24.F6-0355-S02 (@UC9581) — `payment_status_visibility`: AI được xem danh sách
 * "ai chưa đóng" (tên + trạng thái, KHÔNG số tiền) của `GET /v1/collections/:id/members-status`.
 *
 * 🔴 KHÔNG GỘP VÀO `transparency_mode`. Key đó quản **số tiền** (số dư, thu/chi); key này quản
 * **tên người + trạng thái đóng** — `members-status` không trả một đồng nào. Hai trục ĐỘC LẬP
 * thật: một nhóm có thể muốn *giấu số dư nhưng hiện tên ai chưa đóng*, hoặc ngược lại. Gộp =
 * xoá mất một lựa chọn.
 *
 * QĐ PO 06/08/2026: default `managers_only` (FAIL-CLOSED, khớp CLAUDE.md §7 P5.3 *"không bêu tên
 * trong nhóm"*); nhóm nào muốn minh bạch thì **tự bật** `all_members` trong cài đặt quỹ.
 * --------------------------------------------------------------------------- */
export const PAYMENT_STATUS_VISIBILITY_KEY = "payment_status_visibility";
export const PAYMENT_STATUS_VISIBILITY = {
  /** CHỈ OWNER/ADMIN/TREASURER xem được danh sách ai chưa đóng (mặc định — fail-closed). */
  MANAGERS_ONLY: "managers_only",
  /** Mọi member ACTIVE của nhóm xem được (nhóm chủ động chọn minh bạch). */
  ALL_MEMBERS: "all_members",
} as const;
export type PaymentStatusVisibility =
  (typeof PAYMENT_STATUS_VISIBILITY)[keyof typeof PAYMENT_STATUS_VISIBILITY];
export const paymentStatusVisibilityPatchSchema = z.enum(["managers_only", "all_members"]);

/* ---------------------------------------------------------------------------
 * US-E24.F22-0377 (@UC9657 @UC9658) — `collection_amount_visibility`: MEMBER thường có được thấy
 * **TỔNG TIỀN ĐÃ THU** của một đợt không (card quỹ · tiến độ đợt).
 *
 * 🔴 TRỤC THỨ BA, không gộp với hai khoá đang có:
 *   - `transparency_mode`            → số dư quỹ + thu/chi tổng thể
 *   - `payment_status_visibility`    → TÊN người + trạng thái đóng (không một đồng nào)
 *   - `collection_amount_visibility` → SỐ TIỀN đã thu của một đợt   ← khoá này
 * Gộp bất kỳ hai khoá nào là xoá mất một lựa chọn có thật: nhóm muốn *hiện tên nhưng giấu tiền*,
 * hoặc *hiện tiền nhưng giấu tên*.
 *
 * QĐ PO 09/08/2026: **mặc định `managers_only` (FAIL-CLOSED)**, nhóm tự bật `all_members`. Lý do:
 * nhóm nhỏ thì TỔNG chính là DANH SÁCH — quỹ 3 người, suất 350k, "đã thu 700k" ⇒ ai cũng suy ra
 * đúng một người chưa đóng, cộng "tôi đã đóng" là biết đích danh. Đó là thứ §7 P5.3 cấm.
 * --------------------------------------------------------------------------- */
export const COLLECTION_AMOUNT_VISIBILITY_KEY = "collection_amount_visibility";
export const COLLECTION_AMOUNT_VISIBILITY = {
  /** CHỈ OWNER/ADMIN/TREASURER thấy số tiền đã thu (mặc định — fail-closed). */
  MANAGERS_ONLY: "managers_only",
  /** Mọi member ACTIVE thấy tổng đã thu của đợt (nhóm chủ động chọn minh bạch). */
  ALL_MEMBERS: "all_members",
} as const;
export type CollectionAmountVisibility =
  (typeof COLLECTION_AMOUNT_VISIBILITY)[keyof typeof COLLECTION_AMOUNT_VISIBILITY];
export const collectionAmountVisibilityPatchSchema = z.enum([
  "managers_only",
  "all_members",
]);

/** 403 khi config = `managers_only` và người gọi KHÔNG phải vai quản trị quỹ. */
export const PAYMENT_STATUS_HIDDEN_CODE = "PAYMENT_STATUS_VISIBILITY_RESTRICTED";
export const PAYMENT_STATUS_HIDDEN_MESSAGE =
  "Danh sách thành viên chưa đóng chỉ dành cho chủ nhóm, quản trị viên và thủ quỹ. " +
  "Nhóm có thể bật công khai trong cài đặt quỹ.";

/** ai_token_budget_monthly: trần token AI mỗi nhóm/tháng (int [0,100M]; 0 = tắt cost-guard). AI. */
export const AI_TOKEN_BUDGET_MONTHLY_KEY = "ai_token_budget_monthly";
export const AI_TOKEN_BUDGET_MONTHLY_MIN = 0;
export const AI_TOKEN_BUDGET_MONTHLY_MAX = 100_000_000;
export const aiTokenBudgetMonthlyPatchSchema = z
  .number()
  .int()
  .min(AI_TOKEN_BUDGET_MONTHLY_MIN)
  .max(AI_TOKEN_BUDGET_MONTHLY_MAX);

/**
 * Body cho PATCH /v1/funds/:id/config. Thiết kế EXTENSIBLE: mỗi key optional + `.strict()` +
 * refine "ít nhất 1 key" (PATCH rỗng → 400). 0034 bồi đắp expense keys; 0084 bồi đắp 5 key
 * group-overridable (transparency/reminder/overdue/pay-on-behalf/ai-budget).
 *
 * superRefine enforce ranh giới mode↔sub-config (AC2 "vote phải config tỉ lệ + thời hạn bắt buộc"):
 *  - mode=ratio ⇒ threshold + deadline PHẢI có mặt (thiếu → 400).
 *  - mode=admin_only|majority ⇒ threshold/deadline KHÔNG được set (sub-config mồ côi → 400).
 *  - threshold/deadline mà KHÔNG có mode ⇒ 400 (sub-config mồ côi).
 */
export const fundConfigPatchSchema = z
  .object({
    paymentConfirmationMode: paymentConfirmationModeSchema.optional(),
    expenseDecisionMode: expenseDecisionModeSchema.optional(),
    expenseDecisionThreshold: expenseDecisionThresholdSchema.optional(),
    voteMinDeadlineHours: voteMinDeadlineHoursSchema.optional(),
    // 0084 — 5 key group-overridable bổ sung (flat optional, không cross-field như ratio).
    transparencyMode: transparencyModePatchSchema.optional(),
    reminderDaysBefore: reminderDaysBeforePatchSchema.optional(),
    overdueDailyReminder: overdueDailyReminderPatchSchema.optional(),
    payOnBehalfCreatesDebt: payOnBehalfCreatesDebtPatchSchema.optional(),
    aiTokenBudgetMonthly: aiTokenBudgetMonthlyPatchSchema.optional(),
    // US-E24.F6-0355-S02 (@UC9581) — ai xem được danh sách "ai chưa đóng".
    paymentStatusVisibility: paymentStatusVisibilityPatchSchema.optional(),
    /**
     * 🔴 US-E24.F44-0458-S02 (@UC9657 · QĐ người dùng 19/08/2026) — ai xem được TỔNG ĐÃ THU của đợt.
     *
     * Khoá này TỒN TẠI từ 0377 nhưng CHƯA BAO GIỜ có đường sửa: không ở đây, không ở whitelist
     * escalation của SUPER_ADMIN. Nghĩa là giá trị nào ghi vào `fund.settings` lúc tạo quỹ là
     * VĨNH VIỄN — trưởng nhóm không đổi được, quản trị hệ thống cũng không, và sửa mặc định hệ
     * thống cũng vô ích vì override cấp quỹ luôn thắng.
     *
     * Trước bản này chưa ai chạm nên không lộ. Nay quỹ tạo mới ghi sẵn `all_members`, tức mọi quỹ
     * đều bị đóng đinh — nhóm muốn quay lại kín thì không có cửa nào. Mở cửa sửa ở đây là điều kiện
     * để việc "mặc định mở" vẫn là MỘT MẶC ĐỊNH chứ không phải một quyết định không thể rút lại.
     */
    collectionAmountVisibility: collectionAmountVisibilityPatchSchema.optional(),
  })
  .strict()
  .refine((body) => Object.keys(body).length > 0, {
    message: "Cần ít nhất 1 thiết lập để cập nhật",
  })
  .superRefine((body, ctx) => {
    const hasThreshold = body.expenseDecisionThreshold !== undefined;
    const hasDeadline = body.voteMinDeadlineHours !== undefined;

    if (body.expenseDecisionMode === EXPENSE_DECISION_MODE.RATIO) {
      // AC2: vote (ratio) BẮT BUỘC tỉ lệ + thời hạn.
      if (!hasThreshold) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["expenseDecisionThreshold"],
          message: "Vote phải cấu hình tỉ lệ thông qua (%)",
        });
      }
      if (!hasDeadline) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["voteMinDeadlineHours"],
          message: "Vote phải cấu hình thời hạn (giờ)",
        });
      }
      return;
    }

    // mode ≠ ratio (admin_only/majority) HOẶC không có mode ⇒ sub-config KHÔNG được set.
    if (hasThreshold || hasDeadline) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["expenseDecisionThreshold"],
        message: "Tỉ lệ/thời hạn chỉ áp dụng khi chọn vote (ratio)",
      });
    }
  });

export type FundConfigPatchRequest = z.infer<typeof fundConfigPatchSchema>;

/**
 * Shape response cho effective config 1 quỹ (fund override > system default). Trả CẢ payment +
 * expense effective config. `.strict()` chặn rò field thừa (C2). threshold/deadline optional —
 * chỉ có mặt khi mode=ratio.
 */
export const fundConfigResponseSchema = z
  .object({
    paymentConfirmationMode: paymentConfirmationModeSchema,
    expenseDecisionMode: expenseDecisionModeSchema,
    expenseDecisionThreshold: expenseDecisionThresholdSchema.optional(),
    voteMinDeadlineHours: voteMinDeadlineHoursSchema.optional(),
    // 0084 — 5 key group-overridable: LUÔN có mặt trong effective (resolver fail-closed về default).
    transparencyMode: transparencyModePatchSchema,
    reminderDaysBefore: reminderDaysBeforePatchSchema,
    overdueDailyReminder: overdueDailyReminderPatchSchema,
    payOnBehalfCreatesDebt: payOnBehalfCreatesDebtPatchSchema,
    aiTokenBudgetMonthly: aiTokenBudgetMonthlyPatchSchema,
    // US-E24.F6-0355-S02 (@UC9581) — LUÔN có mặt (resolver fail-closed về `managers_only`).
    paymentStatusVisibility: paymentStatusVisibilityPatchSchema,
  })
  .strict();

export type FundConfigResponse = z.infer<typeof fundConfigResponseSchema>;

/* ===========================================================================
 * PUT /v1/funds/:id/bank-account — Configure Fund Bank Account (text fallback, optional)
 * PUT /v1/funds/:id/qr-image      — Upload ảnh QR nhận tiền (nguồn chính) lên R2
 * GET /v1/funds/:id/qr            — Serve ảnh QR đã upload (presigned) + bank-text fallback
 * (US-E5.F2-0107 / @UC0030 · @UC0057 — thay VietQR/Napas bằng user upload ảnh QR).
 *
 * QUYẾT ĐỊNH SẢN PHẨM (0107 — thay US-E5.F2-0032 VietQR): bỏ HẲN Napas VietQR (external dependency →
 * dev 503). Owner mở app ngân hàng lấy ảnh QR nhận tiền TĨNH → upload lên Goppy (R2, như avatar/proof).
 * Member xem = ảnh QR đã upload (presigned GET) + tự nhập số tiền. bankAccount = metadata quỹ (ảnh QR
 * key + STK-text optional), ghi THẲNG Fund.bankAccount jsonb (additive qrImageKey — KHÔNG migration).
 *
 * BẢO MẬT / RANH GIỚI (invariants):
 *  - PII (C2): ảnh QR chứa STK/tên chủ TK → R2 encrypted-at-rest, serve presigned. accountNumber/
 *    accountName KHÔNG log thô; audit redact (chỉ fundId + hành động). Upload magic-byte sniff (KHÔNG
 *    tin content-type header) + size cap ≤5MB.
 *  - Validate biên (C5): bank-text OPTIONAL (QR là nguồn chính); khi CÓ vẫn validate (bankBin ∈ napas,
 *    STK numeric 6–19, tên non-empty). `.strict()` chặn over-post (KHÔNG nhận field lạ vào jsonb).
 *  - RBAC OWNER/ADMIN (C4): upload/bank-text = @RequireFundRoles(OWNER,ADMIN) trên FundAccessGuard
 *    (member/treasurer → 403); GET /qr = member+ (mọi member đọc để CK). non-member/cross-tenant/
 *    non-UUID → 404 đồng nhất (chống IDOR).
 *  - Honest: chưa upload QR → GET /qr trả 409 (KHÔNG bịa QR). ZERO external Napas call.
 *  - KHÔNG ghi ledger / KHÔNG số tiền — QR chỉ là ảnh CK thủ công (không B1).
 * ========================================================================= */

/**
 * Danh mục bankBin napas (VietQR) — subset ngân hàng VN phổ biến. Out-of-list → 400 (PO chốt: bankBin
 * phải hợp lệ). Curated, KHÔNG đầy đủ; TODO: đồng bộ danh mục napas đầy đủ + checksum/per-bank rule.
 * Một nguồn sự thật api ↔ mobile (mobile render dropdown chọn ngân hàng).
 */
export const NAPAS_BANK_BINS = {
  VIETCOMBANK: "970436",
  TECHCOMBANK: "970407",
  BIDV: "970418",
  VIETINBANK: "970415",
  MB: "970422",
  ACB: "970416",
  VPBANK: "970432",
  TPBANK: "970423",
  SACOMBANK: "970403",
  AGRIBANK: "970405",
  VIB: "970441",
  SHB: "970443",
  HDBANK: "970437",
  OCB: "970448",
  MSB: "970426",
} as const;

/** Tập hợp BIN hợp lệ (O(1) lookup) — dùng ở schema refine + service validate. */
export const VALID_BANK_BINS: ReadonlySet<string> = new Set(Object.values(NAPAS_BANK_BINS));

/** Biên độ dài số tài khoản (PO chốt: numeric 6–19). */
export const ACCOUNT_NUMBER_MIN_LENGTH = 6;
export const ACCOUNT_NUMBER_MAX_LENGTH = 19;
/** Giới hạn độ dài tên chủ tài khoản (chống over-long; đủ cho tên hợp lệ). */
export const ACCOUNT_NAME_MAX_LENGTH = 100;

/** Thông điệp lỗi validate bank — một nguồn sự thật api ↔ mobile (lỗi inline field). */
export const BANK_BIN_REQUIRED_MESSAGE = "Vui lòng chọn ngân hàng";
export const BANK_BIN_INVALID_MESSAGE = "Ngân hàng không hợp lệ";
export const ACCOUNT_NUMBER_REQUIRED_MESSAGE = "Vui lòng nhập số tài khoản";
export const ACCOUNT_NUMBER_INVALID_MESSAGE = "Số tài khoản phải gồm 6–19 chữ số";
export const ACCOUNT_NAME_REQUIRED_MESSAGE = "Vui lòng nhập tên chủ tài khoản";

/** Giới hạn độ dài tên ngân hàng hiển thị (free-text/display-only — KHÔNG dùng generate). */
export const BANK_NAME_MAX_LENGTH = 100;

/**
 * Body cho PUT /v1/funds/:id/bank-account (US-E5.F2-0107 / @UC0030 — QR upload thay VietQR).
 *
 * QUYẾT ĐỊNH SẢN PHẨM (0107): ảnh QR upload là NGUỒN CHÍNH cấu hình nhận tiền; STK-text (bankBin/
 * accountNumber/accountName) chỉ là FALLBACK optional (member copy khi không quét được QR). Vì vậy
 * TẤT CẢ field text đều OPTIONAL. Khi CÓ mặt vẫn validate như cũ (bankBin ∈ napas, STK numeric 6–19,
 * tên non-empty ≤100). Thêm bankName optional (tên ngân hàng hiển thị — display-only, không generate).
 *  - `.strict()` chặn over-post (chống ghi rác key lạ vào Fund.bankAccount jsonb — C5).
 *  - Body rỗng (không field nào) HỢP LỆ → chỉ khi chưa upload QR thì needsConfig vẫn true (QR là nguồn
 *    chính); ghi bank-text rỗng KHÔNG tự cấu hình quỹ.
 */
export const fundBankAccountSchema = z
  .object({
    bankBin: z
      .string()
      .trim()
      .min(1, BANK_BIN_REQUIRED_MESSAGE)
      .refine((v) => VALID_BANK_BINS.has(v), BANK_BIN_INVALID_MESSAGE)
      .optional(),
    accountNumber: z
      .string()
      .trim()
      .min(1, ACCOUNT_NUMBER_REQUIRED_MESSAGE)
      // TODO: checksum / per-bank length rule (PO chốt slice tối thiểu numeric 6–19).
      .regex(
        new RegExp(`^\\d{${ACCOUNT_NUMBER_MIN_LENGTH},${ACCOUNT_NUMBER_MAX_LENGTH}}$`),
        ACCOUNT_NUMBER_INVALID_MESSAGE,
      )
      .optional(),
    accountName: z
      .string()
      .trim()
      .min(1, ACCOUNT_NAME_REQUIRED_MESSAGE)
      .max(ACCOUNT_NAME_MAX_LENGTH)
      .optional(),
    // 0107: tên ngân hàng hiển thị (free-text). Display-only — KHÔNG dùng để generate QR.
    bankName: z.string().trim().max(BANK_NAME_MAX_LENGTH).optional(),
  })
  .strict();

export type FundBankAccountRequest = z.infer<typeof fundBankAccountSchema>;

/**
 * Response cho PUT bank-account (echo bank-text đã lưu để client xác nhận). Field OPTIONAL (chỉ có
 * mặt khi owner đã nhập). needsConfig phản ánh QR đã upload chưa (nguồn chính) — KHÔNG phải bank-text.
 * Chủ TK xem được STK mình vừa nhập (KHÔNG mask ở response — mask chỉ ở LOG/AUDIT). `.strict()`.
 */
export const fundBankAccountResponseSchema = z
  .object({
    bankBin: z.string().optional(),
    accountNumber: z.string().optional(),
    accountName: z.string().optional(),
    bankName: z.string().optional(),
    needsConfig: z.boolean(),
  })
  .strict();

export type FundBankAccountResponse = z.infer<typeof fundBankAccountResponseSchema>;

/**
 * Response cho GET /v1/funds/:id/qr (US-E5.F2-0107 / @UC0030 · @UC0057) — ảnh QR do owner upload lên
 * R2, serve presigned GET (KHÔNG gọi Napas). QR TĨNH: member quét rồi TỰ nhập số tiền (không điền sẵn).
 *  - qrImageUrl: presigned GET URL / public base URL trỏ ảnh QR đã upload (client render Image.network).
 *  - accountNumber/accountName/bankName: STK-text fallback optional (member copy khi không quét được).
 *  - transferContent: nội dung CK gợi ý optional (client Copy) — deterministic từ ngữ cảnh nếu có.
 * `.strict()` chặn rò field thừa (C2). Chưa upload QR → controller trả 409 (KHÔNG bịa qrImageUrl).
 */
export const fundQrResponseSchema = z
  .object({
    qrImageUrl: z.string().min(1),
    accountNumber: z.string().optional(),
    accountName: z.string().optional(),
    bankName: z.string().optional(),
    transferContent: z.string().nullable().optional(),
  })
  .strict();

export type FundQrResponse = z.infer<typeof fundQrResponseSchema>;

/**
 * Response cho PUT /v1/funds/:id/qr-image — sau khi upload ảnh QR thành công, trả presigned URL để
 * client hiển thị ngay preview. `.strict()`.
 */
export const fundQrUploadResponseSchema = z
  .object({
    qrImageUrl: z.string().min(1),
  })
  .strict();

export type FundQrUploadResponse = z.infer<typeof fundQrUploadResponseSchema>;
